import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../context/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiAlertCircle, FiPackage, FiChevronDown, FiChevronUp, 
  FiAlertTriangle, FiSend, FiRotateCcw, FiLoader 
} = FiIcons;

const ResultsList = ({ results }) => {
  const { incidents, currentProject, manageUnlinkedGarment } = useData();
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [showAllResults, setShowAllResults] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  const isManager = currentProject?.role === 'manager';

  const INITIAL_RESULTS_COUNT = 10;
  
  // Helper para identificar prendas "Problemáticas" (Unlinked + 9)
  const isUnlinked9 = (garment) => {
    const meta = garment.metadata || {};
    const status = meta.GarmentStatus || '';
    const barcode = String(garment.barcode || '');
    return status.toLowerCase().includes('unlinked') && barcode.startsWith('9');
  };

  // Helper para saber si debemos mostrar la prenda según el rol
  const shouldShowGarment = (garment) => {
    const status = garment.metadata?.unlinked_status;
    if (status === 'sent') return false; 
    
    if (isUnlinked9(garment)) {
      if (status === 'withdrawn' && !isManager) return false;
    }
    
    return true;
  };

  // Lógica de Ordenación
  const getWardrobeOrder = (wardrobeName) => {
    const wardrobe = String(wardrobeName || '').toLowerCase();
    if (wardrobe.includes('nave 9')) return 1000;
    if (wardrobe.includes('nave 11')) return 1001;
    const vMatch = wardrobe.match(/^v(\d+)$/);
    if (vMatch) {
      const num = parseInt(vMatch[1]);
      if (num === 18) return 15;
      if (num >= 32 && num <= 35) return 16 + (num - 32);
      return num;
    }
    return 9999;
  };

  const sortedResults = [...results].sort((a, b) => {
    const wA = a.wardrobeOrder ?? getWardrobeOrder(a.wardrobeName);
    const wB = b.wardrobeOrder ?? getWardrobeOrder(b.wardrobeName);
    if (wA !== wB) return wA - wB;
    return new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
      .compare(String(a.locker || ''), String(b.locker || ''));
  });

  const visibleResults = showAllResults ? sortedResults : sortedResults.slice(0, INITIAL_RESULTS_COUNT);
  const hasMoreResults = results.length > INITIAL_RESULTS_COUNT && !showAllResults;

  const toggleExpanded = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) newExpanded.delete(itemId);
    else newExpanded.add(itemId);
    setExpandedItems(newExpanded);
  };

  const handleAction = async (barcode, action) => {
    if (!barcode || processingId) return;
    setProcessingId(barcode);
    try {
      await manageUnlinkedGarment(barcode, action);
    } catch (error) {
      alert('Error al procesar la acción');
    } finally {
      setProcessingId(null);
    }
  };

  if (results.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
        <SafeIcon icon={FiPackage} className="text-4xl text-gray-200 mx-auto mb-4" />
        <p className="text-gray-400 font-medium tracking-tight">No se encontraron resultados</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 pb-24">
      {visibleResults.map((item, index) => {
        const incidentCount = incidents.filter(inc => inc.employeeCode === item.employeeCode).length;
        const hasUnlinkedWarning = item.garments?.some(g => isUnlinked9(g) && shouldShowGarment(g));
        const isExpanded = expandedItems.has(item.id);
        const visibleGarments = item.garments?.filter(shouldShowGarment) || [];

        return (
          <motion.div 
            key={item.id} 
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: index * 0.05 }} 
            className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 relative overflow-hidden"
          >
            {/* CABECERA DE EMPLEADO */}
            <div className="space-y-3" onClick={() => toggleExpanded(item.id)}>
              <div className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-2 overflow-hidden">
                  <h3 className="font-bold text-gray-800 text-base uppercase truncate">
                    {item.owner || 'Sin nombre'}
                  </h3>
                  {hasUnlinkedWarning && (
                    <motion.div 
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                      className="bg-red-100 text-red-600 p-1 rounded-full shrink-0"
                    >
                      <SafeIcon icon={FiAlertTriangle} className="text-[10px]" />
                    </motion.div>
                  )}
                </div>

                {incidentCount > 0 && (
                  <div className="flex items-center space-x-1 bg-red-50 px-2 py-0.5 rounded-full shrink-0 border border-red-100">
                    <SafeIcon icon={FiAlertCircle} className="text-[10px] text-red-600" />
                    <span className="text-[10px] text-red-600 font-black">{incidentCount}</span>
                  </div>
                )}
              </div>

              {/* STATS RÁPIDOS */}
              <div className="grid grid-cols-3 gap-2 text-center">
                {[
                  { label: 'Nº EMP', value: item.employeeCode },
                  { label: 'VEST', value: item.wardrobeName },
                  { label: 'TAQ', value: item.locker }
                ].map((stat, idx) => (
                  <div key={idx} className="flex flex-col bg-gray-50/80 rounded-xl py-1.5 border border-gray-100/50">
                    <span className="text-[8px] text-gray-400 font-black uppercase tracking-widest">{stat.label}</span>
                    <span className="font-bold text-gray-700 text-xs">{stat.value}</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-center pt-1">
                 <button className="text-[10px] text-red-500 font-black flex items-center gap-1 hover:text-red-700 uppercase tracking-tighter">
                    {isExpanded ? 'Ocultar' : `Ver prendas (${visibleGarments.length})`}
                    <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} />
                 </button>
              </div>
            </div>

            {/* DETALLE DE PRENDAS */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }} 
                  animate={{ opacity: 1, height: 'auto' }} 
                  exit={{ opacity: 0, height: 0 }} 
                >
                  <div className="pt-4 border-t border-gray-50 mt-3 space-y-3">
                    {visibleGarments.map((garment, gIdx) => {
                      const isProblematic = isUnlinked9(garment);
                      const unlinkedStatus = garment.metadata?.unlinked_status;
                      const isWithdrawn = unlinkedStatus === 'withdrawn';
                      const displayBarcode = garment.barcode?.startsWith('MISSING-') ? 'S/C' : garment.barcode;

                      if (isProblematic) {
                        return (
                          <div 
                            key={gIdx} 
                            className={`rounded-xl p-3 border-2 relative overflow-hidden transition-colors ${
                              isWithdrawn ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200'
                            }`}
                          >
                            {processingId === garment.barcode && (
                              <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center backdrop-blur-[1px]">
                                <SafeIcon icon={FiLoader} className="animate-spin text-red-600" />
                              </div>
                            )}

                            <div className="flex justify-between items-start mb-2">
                              <span className="font-black text-red-950 text-xs uppercase tracking-tight pr-2">
                                {garment.garment}
                              </span>
                              {garment.size && (
                                <span className="w-6 h-6 rounded-lg bg-red-600 text-white text-[10px] font-black flex items-center justify-center shadow-sm">
                                  {garment.size}
                                </span>
                              )}
                            </div>

                            <div className="bg-white/40 rounded-lg p-2 mb-3 border border-red-100/30">
                              <div className="flex justify-between text-[9px] font-bold">
                                <span className="text-gray-400 uppercase">Barcode</span>
                                <span className="text-red-800 font-mono">{displayBarcode}</span>
                              </div>
                            </div>

                            {/* ACCIONES OPERATIVAS */}
                            <div className="grid grid-cols-2 gap-2">
                              {!isWithdrawn ? (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleAction(garment.barcode, 'withdrawn'); }}
                                  className="flex items-center justify-center gap-1 bg-white border border-red-200 text-red-700 py-2 rounded-lg text-[9px] font-black shadow-sm active:scale-95"
                                >
                                  <SafeIcon icon={FiPackage} /> RETIRADA
                                </button>
                              ) : (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleAction(garment.barcode, 'undo_withdrawn'); }}
                                  className="flex items-center justify-center gap-1 bg-white border border-orange-300 text-orange-700 py-2 rounded-lg text-[9px] font-black shadow-sm active:scale-95"
                                >
                                  <SafeIcon icon={FiRotateCcw} /> CANCELAR
                                </button>
                              )}

                              {isManager ? (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleAction(garment.barcode, 'sent'); }}
                                  className="flex items-center justify-center gap-1 bg-red-600 text-white py-2 rounded-lg text-[9px] font-black shadow-sm active:scale-95"
                                >
                                  <SafeIcon icon={FiSend} /> ENVIADA
                                </button>
                              ) : (
                                <div className="flex items-center justify-center text-[8px] text-gray-400 font-bold bg-gray-100/50 rounded-lg border border-dashed border-gray-200">
                                  MANAGER REQ.
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }

                      {/* PRENDA NORMAL */}
                      return (
                        <div key={gIdx} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-xs font-bold text-gray-600 uppercase">
                              {garment.garment}
                            </span>
                            {garment.size && (
                              <span className="w-5 h-5 rounded-md bg-gray-200 text-gray-600 text-[9px] font-bold flex items-center justify-center">
                                {garment.size}
                              </span>
                            )}
                          </div>
                          <div className="flex justify-between text-[9px] font-medium text-gray-400">
                            <span>{displayBarcode}</span>
                            <span className="font-mono">{garment.rfid || garment.metadata?.RFID || 'No RFID'}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}

      {hasMoreResults && (
        <div className="text-center pt-4">
          <button 
            onClick={() => setShowAllResults(true)} 
            className="px-8 py-2.5 bg-white border border-gray-200 text-gray-500 rounded-full text-[10px] font-black hover:bg-gray-50 transition-all shadow-sm uppercase tracking-widest"
          >
            Mostrar todos ({results.length})
          </button>
        </div>
      )}
    </div>
  );
};

export default ResultsList;